import Container from "../Common/Container";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLoading } from "@/context/loading";
import { useRouter } from "next/router";

export default function Hero() {
  const { isLoading } = useLoading();
  const router = useRouter();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [homeTitleData, setHomeTitleData] = useState({
    title: "More Live Shows",
    subtitle: "For local artists & fans",
    description: "Artists, bands and DJs get booked for shows and fans discover new talent every week.",
  });

  useEffect(() => {
    if (!isLoading) {
      setShouldAnimate(true);
    }
  }, [isLoading]);

  useEffect(() => {
    const fetchHomeTitle = async () => {
      try {
        const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!BACKEND_URL) {
          console.error("BACKEND_URL is not defined");
          return;
        }

        const response = await fetch(`${BACKEND_URL}/webapi/manageHomeTitle`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.status && result.data) {
            setHomeTitleData({
              title: result.data.title || "More Live Shows",
              subtitle: result.data.subtitle || "For local artists & fans",
              description: result.data.description || "Artists, bands and DJs get booked for shows and fans discover new talent every week.",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching home title data:", error);
        // Keep default values on error
      }
    };

    fetchHomeTitle();
  }, []);

  useEffect(() => {
    const playVideo = () => {
      const vid = document.getElementById("heroVideo");
      if (vid && vid.paused) {
        vid.play().catch(() => {});
      }
      document.removeEventListener("touchstart", playVideo);
    };

    document.addEventListener("touchstart", playVideo);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.8,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 10
      }
    }
  };

  const videoVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.8
      }
    }
  };

  return (
    <div className="">
      <div className="layout_bg_image hidden lg:block">
        <span className="bg_inner_shadow"></span>
      </div>
      <Container className="px-6 lg:pl-[75px] lg:pr-[48px] lg:pt-[100px] lg:pb-[89px]">
        <div className="flex flex-col relative z-[2] lg:flex-row text-white items-start lg:gap-[54px]">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={shouldAnimate ? "visible" : "hidden"}
            className="w-full order-1 lg:py-[50px] lg:max-w-[40%]"
          >
            <div className="px-[13px] lg:px-0 lg:pr-4 lg:pb-3">
              <motion.p 
                variants={itemVariants}
                className="text-[24px] leding-[32px] lg:text-[60px] font-urbanist lg:tracking-[1px] font-[800] lg:leading-[54px]"
              >
                {homeTitleData.title}
              </motion.p>
              
              <motion.h1 
                variants={itemVariants}
                className="hidden lg:block lg:mt-7 text-white lg:text-[30px] lg:leading-[40px] lg:py-[2.5px]"
              >
                {homeTitleData.subtitle}
              </motion.h1>

              <motion.p 
                variants={itemVariants}
                className="text-white text-[16px] leading-[8px] my-3 lg:hidden"
              >
                {homeTitleData.subtitle}
              </motion.p>
              
              <motion.p 
                variants={itemVariants}
                className="text-[16px] leading-[22px] lg:mt-7 text-[#FFFFFF] lg:text-[30px] lg:leading-[40px] lg:tracking-[1px]"
              >
                {homeTitleData.description}
              </motion.p>

              <motion.div
                variants={buttonVariants}
                className="mt-7 w-full flex flex-row gap-6"
              >
                <button
                  onClick={() => {
                    window.open("https://play.qoncertapp.com/more-shows", "_blank");
                  }}
                  className="py-2 w-[190.88px] min-w-max max-h-[54px] font-extrabold text-[20px] px-7 text-black bg-white rounded-2xl hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  Book a Show
                </button>
                <button
                  onClick={() => router.push("/shows")}
                  className="py-2 w-[190.88px] min-w-max max-h-[54px] font-extrabold text-[20px] px-7 text-white bg-transparent rounded-2xl border border-white/70 hover:border-white hover:bg-white/10 transition-colors"
                  type="button"
                >
                  Get Tickets
                </button>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            variants={videoVariants}
            initial="hidden"
            animate={shouldAnimate ? "visible" : "hidden"}
            className="w-full lg:mt-[60px] h-full lg:order-2"
          >
            <div className="w-full lg:w-[740px] h-full max-h-[380px] overflow-hidden rounded-[16px]">
              <video
                id="heroVideo"
                className="w-full h-full object-cover rounded-[16px]"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={"/assets/video.mp4"} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>
        </div>
      </Container>
    </div>
  );
}
